import type Konva from "konva";
import { useEffect, useMemo, useRef } from "react";
import { Group, Image, Rect } from "react-konva";

import { useImagesFastCache } from "@design-lab/entities/artwork";
import type { AreaOfInterest, BoundingBox2D, MaskObject } from "@design-lab/entities/pinning";

const WHITE_THRESHOLD = 200;

/**
 * Konva filter that makes white pixels transparent and sets non-white to 70% opacity
 */
const maskTransparencyFilter = (imageData: ImageData) => {
  const { data } = imageData;
  const len = data.length;

  for (let i = 0; i < len; i += 4) {
    const r = data[i] as number;
    const g = data[i + 1] as number;
    const b = data[i + 2] as number;

    if (r >= WHITE_THRESHOLD && g >= WHITE_THRESHOLD && b >= WHITE_THRESHOLD) {
      // White pixels (area of interest) -> transparent
      data[i + 3] = 0;
    } else {
      // Non-white pixels -> 70% opacity overlay for stronger contrast
      data[i + 3] = 178;
    }
  }
};

type ArtworkDimensions = {
  x: number;
  y: number;
  width: number;
  height: number;
};

type AreasOfInterestOverlayProps = {
  areasOfInterest: AreaOfInterest;
  artworkDimensions: ArtworkDimensions;
  artworkImage: HTMLImageElement;
  isVisible: boolean;
  segmentationMaskUrl?: string | undefined; // Internal URL for persisted mask (overrides maskUrl in areasOfInterest)
};

function isBoundingBox2D(obj: BoundingBox2D | MaskObject | null | undefined): obj is BoundingBox2D {
  return obj != null && "bbox2d" in obj && Array.isArray(obj.bbox2d);
}

function isMaskObject(obj: BoundingBox2D | MaskObject | null | undefined): obj is MaskObject {
  return obj != null && "maskUrl" in obj;
}

type TransformedBox = {
  x: number;
  y: number;
  width: number;
  height: number;
};

function transformBbox2D(
  bbox: BoundingBox2D,
  artworkDimensions: ArtworkDimensions,
  naturalSize: { width: number; height: number }
): TransformedBox {
  const [x1, y1, x2, y2] = bbox.bbox2d;
  const scaleX = artworkDimensions.width / Math.max(naturalSize.width, 1);
  const scaleY = artworkDimensions.height / Math.max(naturalSize.height, 1);

  return {
    x: artworkDimensions.x + x1 * scaleX,
    y: artworkDimensions.y + y1 * scaleY,
    width: (x2 - x1) * scaleX,
    height: (y2 - y1) * scaleY
  };
}

function MaskHighlight({
  maskObject,
  artworkDimensions,
  maskUrlOverride
}: {
  maskObject: MaskObject;
  artworkDimensions: ArtworkDimensions;
  maskUrlOverride?: string | undefined;
}) {
  const maskUrl = maskUrlOverride ?? maskObject.maskUrl;
  const [cachedImage] = useImagesFastCache([maskUrl]);
  const imageRef = useRef<Konva.Image>(null);

  // Apply filter and cache when image loads or dimensions change
  useEffect(() => {
    const node = imageRef.current;
    if (node && cachedImage?.image) {
      node.clearCache();
      node.cache();
      node.getLayer()?.batchDraw();
    }
  }, [cachedImage?.image, artworkDimensions]);

  if (!cachedImage?.image) return null;

  return (
    <Image
      ref={imageRef}
      image={cachedImage.image}
      x={artworkDimensions.x}
      y={artworkDimensions.y}
      width={artworkDimensions.width}
      height={artworkDimensions.height}
      filters={[maskTransparencyFilter]}
      listening={false}
    />
  );
}

function BoundingBoxOverlay({
  artworkDimensions,
  transformedBoxes
}: {
  artworkDimensions: ArtworkDimensions;
  transformedBoxes: TransformedBox[];
}) {
  const groupRef = useRef<Konva.Group>(null);

  // Cache the group to enable composite operations - clear and re-cache when dimensions change
  useEffect(() => {
    const node = groupRef.current;
    if (node && transformedBoxes.length > 0) {
      node.clearCache();
      node.cache();
      node.getLayer()?.batchDraw();
    }
    return () => {
      node?.clearCache();
    };
  }, [transformedBoxes, artworkDimensions]);

  if (transformedBoxes.length === 0) return null;

  return (
    <Group ref={groupRef} listening={false}>
      {/* Dark overlay covering the artwork */}
      <Rect
        x={artworkDimensions.x}
        y={artworkDimensions.y}
        width={artworkDimensions.width}
        height={artworkDimensions.height}
        fill="rgba(0, 0, 0, 0.7)"
        listening={false}
      />
      {/* Punch transparent holes for each bounding box */}
      {transformedBoxes.map((box, index) => (
        <Rect
          key={`cutout-${index}`}
          x={box.x}
          y={box.y}
          width={box.width}
          height={box.height}
          fill="black"
          globalCompositeOperation="destination-out"
          listening={false}
        />
      ))}
    </Group>
  );
}

export function AreasOfInterestOverlay({
  areasOfInterest,
  artworkDimensions,
  artworkImage,
  isVisible,
  segmentationMaskUrl
}: AreasOfInterestOverlayProps) {
  const naturalWidth = artworkImage.naturalWidth || artworkImage.width;
  const naturalHeight = artworkImage.naturalHeight || artworkImage.height;

  // Calculate transformed bounding boxes for cutouts
  const transformedBoxes = useMemo(() => {
    if (areasOfInterest.type !== "bounding_box") return [];

    return areasOfInterest.objects
      .filter(isBoundingBox2D)
      .map(bbox =>
        transformBbox2D(bbox, artworkDimensions, { width: naturalWidth, height: naturalHeight })
      );
  }, [areasOfInterest, artworkDimensions, naturalWidth, naturalHeight]);

  if (!isVisible || !areasOfInterest.objects.length) return null;

  return (
    <Group listening={false}>
      {/* Dark overlay with bounding box cutouts */}
      {areasOfInterest.type === "bounding_box" && (
        <BoundingBoxOverlay
          artworkDimensions={artworkDimensions}
          transformedBoxes={transformedBoxes}
        />
      )}

      {/* Render mask highlights */}
      {areasOfInterest.type === "mask" &&
        areasOfInterest.objects.map((obj, index) => {
          if (!isMaskObject(obj)) return null;

          return (
            <MaskHighlight
              key={`mask-${index}`}
              maskObject={obj}
              artworkDimensions={artworkDimensions}
              maskUrlOverride={segmentationMaskUrl}
            />
          );
        })}
    </Group>
  );
}
